import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Flame } from "lucide-react";
import { useApi } from "@/lib/api";
import { SectionHead, NewsCard, AthleteCard, FixtureRow, VideoCard, Loader, Empty, SportBadge } from "@/components/Blocks";
import { SportTiles, AppPromo } from "@/components/Promo";
import { Newsletter } from "@/components/Footer";
import { useT } from "@/lib/i18n";
import { timeAgo, sportColor, FLAG, onImgError } from "@/lib/utils";
import { LionsCountdown, LionsLastResult } from "@/components/Countdown";

const Hero = ({ banners, ticker, lions }) => {
  const { t } = useT();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (banners.length < 2) return;
    const id = setInterval(() => setI((x) => (x + 1) % banners.length), 9000);
    return () => clearInterval(id);
  }, [banners.length]);
  const b = banners[i];
  if (!b) return null;
  return (
    <section className="relative grain min-h-[620px] overflow-hidden bg-[#0B1120] py-16 text-white sm:min-h-[660px]" data-testid="hero-section">
      {banners.map((bn, idx) => (
        <img key={bn.id} src={bn.image} alt="" className={`hero-motion hero-vivid absolute inset-0 h-full w-full object-cover transition-opacity duration-[1500ms] ${idx === i ? "opacity-100" : "opacity-0"}`} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[#052e22]/85 via-[#0B1120]/45 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B1120]/90 to-transparent" />
      <div className="hero-glow absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-[#047857]/40 blur-[120px]" />
      <div className="hero-glow-2 absolute right-10 -top-32 h-[420px] w-[420px] rounded-full bg-[#F5B301]/25 blur-[120px]" />
      <div className="container-x relative grid min-h-[inherit] grid-cols-1 items-center lg:grid-cols-[1fr_340px]">
        <div key={b.id}>
          <p className="rise inline-flex items-center gap-2 rounded-full border border-[#F5B301]/40 bg-[#F5B301]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.3em] text-[#F5B301]" data-testid="hero-label"><Zap size={12} /> {b.label}</p>
          <h1 className="rise rise-1 mt-5 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-[76px]" data-testid="hero-title">
            {b.title_lines.map((l) => <span key={l} className="block drop-shadow-[0_4px_24px_rgba(0,0,0,.45)]">{l}</span>)}
            <span className="block text-gold-shine">{b.accent}</span>
          </h1>
          <p className="rise rise-2 mt-5 max-w-md text-base text-slate-100 md:text-lg" data-testid="hero-subtitle">{b.subtitle}</p>
          <div className="rise rise-3 mt-7 flex flex-wrap gap-3">
            <Link to={b.cta_link} className="btn-gold-glow px-6 py-3 text-base" data-testid="hero-explore-news-btn">{t(b.cta_text)} <ArrowRight size={18} /></Link>
            <Link to="/live-scores" className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3 text-base font-bold backdrop-blur transition-colors hover:bg-white/20" data-testid="hero-live-btn"><span className="live-dot" /> {t("Live Scores")}</Link>
          </div>
          {lions?.fixture ? <LionsCountdown f={lions.fixture} /> : lions && <LionsLastResult f={lions.last_result} refreshing={lions.refreshing} />}
          <div className="mt-8 flex gap-2">
            {banners.map((_, idx) => <button key={idx} onClick={() => setI(idx)} aria-label={`slide ${idx + 1}`} data-testid={`hero-dot-${idx}`} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-10 bg-[#F5B301]" : "w-4 bg-white/40 hover:bg-white/70"}`} />)}
          </div>
        </div>
        <div className="hidden lg:block rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-md" data-testid="hero-ticker">
          <p className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#F5B301]"><Flame size={12} /> {t("Trending Now")}</p>
          {ticker.map((n, idx) => (
            <Link key={n.slug} to={`/news/${n.slug}`} className="group flex items-start gap-4 border-b border-white/10 py-4 last:border-0 last:pb-0">
              <span className="font-display text-2xl font-bold leading-none" style={{ color: sportColor(n.sport) }}>0{idx + 1}</span>
              <span className="min-w-0">
                <span className="block text-[10px] font-bold uppercase tracking-wider" style={{ color: sportColor(n.sport) }}>{n.sport}</span>
                <span className="block text-[15px] font-semibold leading-snug text-slate-100 line-clamp-2 group-hover:text-[#F5B301]">{n.title}</span>
              </span>
              <ArrowRight size={16} className="ml-auto mt-1 flex-none text-white/40 transition-transform group-hover:translate-x-1 group-hover:text-[#F5B301]" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const Ticker = ({ items }) => {
  const { t } = useT();
  const list = [...items, ...items];
  return (
    <div className="relative flex items-stretch overflow-hidden bg-gradient-to-r from-[#047857] via-[#059669] to-[#047857] text-white" data-testid="breaking-ticker">
      <div className="z-10 flex flex-none items-center gap-2 bg-[#F5B301] px-4 text-xs font-bold uppercase tracking-widest text-[#0B1120] sm:px-6"><span className="live-dot" /> {t("Breaking")}</div>
      <div className="marquee flex whitespace-nowrap py-2.5">
        {list.map((n, idx) => (
          <Link key={idx} to={`/news/${n.slug}`} className="mx-6 inline-flex items-center gap-2 text-sm font-semibold hover:text-[#F5B301]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#F5B301]" />{n.title}<span className="text-white/60">· {timeAgo(n.published_at)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const FeatureStory = ({ n }) => (
  <Link to={`/news/${n.slug}`} data-testid={`feature-story-${n.slug}`} className="group relative block overflow-hidden rounded-2xl bg-[#0B1120] text-white shadow-lg sm:row-span-2">
    <img src={n.image} onError={onImgError} alt="" className="h-full min-h-[320px] w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:min-h-full" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/40 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-6">
      <span className="inline-block rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white" style={{ background: sportColor(n.sport) }}>{n.sport}</span>
      <h3 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl group-hover:text-[#F5B301]">{n.title}</h3>
      <p className="mt-2 text-sm text-slate-300 line-clamp-2">{n.summary}</p>
      <p className="mt-3 text-xs text-slate-400">{n.source} · {timeAgo(n.published_at)}</p>
    </div>
  </Link>
);

export default function Home() {
  const { t } = useT();
  const { data, isLoading } = useApi("home", "/home", { refetchInterval: 60000 });
  const { data: lions } = useApi("nextlions", "/fixtures/next-lions", { refetchInterval: 30000 });
  if (isLoading || !data) return <Loader />;
  const importing = data.top_stories.length === 0;
  const [lead, ...others] = data.top_stories;
  return (
    <div data-testid="home-page" className="vivid-media">
      <Hero banners={data.banners} ticker={data.ticker} lions={lions} />
      {data.latest?.length > 0 && <Ticker items={data.latest} />}
      {importing && <div className="container-x py-6"><Empty text="Initial online import is running — real Cameroonian sports data will appear in a moment. Refresh shortly." /></div>}

      <section className="container-x py-10">
        <SectionHead title="Top Stories" to="/news" linkText="View All News" id="top-stories" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {lead && <div className="sm:col-span-2 sm:row-span-2 lg:col-span-2"><FeatureStory n={lead} /></div>}
          {others.map((n) => <NewsCard key={n.slug} item={n} />)}
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#0B1120] py-14 text-white">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#047857]/40 blur-[110px]" />
        <div className="absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-[#F5B301]/20 blur-[100px]" />
        <div className="container-x relative">
          <div className="mb-8 flex items-end justify-between" data-testid="section-explore-sports">
            <h2 className="section-title section-title-light">{t("Explore by Sport")}</h2>
            <Link to="/more" className="flex items-center gap-1 text-sm font-semibold text-slate-300 hover:text-[#F5B301]" data-testid="section-explore-sports-link">{t("View All Sports")} <ArrowRight size={16} /></Link>
          </div>
          <SportTiles sports={data.sports} />
        </div>
      </section>

      <section className="relative overflow-hidden bg-gradient-to-br from-[#047857] via-[#04684c] to-[#0B1120] py-14 text-white">
        <img src={FLAG} alt="" className="pointer-events-none absolute -right-10 -top-10 h-64 rotate-12 opacity-[0.07]" />
        <div className="container-x relative">
          <div className="mb-8 flex items-end justify-between" data-testid="section-stars-abroad">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-[#F5B301]">{t("Flying the flag")}</p>
              <h2 className="section-title section-title-light">{t("Cameroonian Stars Abroad")}</h2>
            </div>
            <Link to="/cameroonians-abroad" className="btn-gold" data-testid="section-stars-abroad-link">{t("View All Stars")} <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">{data.stars_abroad.map((a) => <AthleteCard key={a.slug} a={a} />)}</div>
        </div>
      </section>

      <section className="bg-slate-50 py-14">
        <div className="container-x grid gap-10 lg:grid-cols-2">
          <div className="rounded-2xl border-t-4 border-[#047857] bg-white p-6 shadow-sm">
            <SectionHead title="Latest Results" to="/live-scores" id="latest-results" />
            <div className="space-y-2">{data.results.length ? data.results.map((f) => <FixtureRow key={f.id} f={f} />) : <Empty />}</div>
          </div>
          <div className="rounded-2xl border-t-4 border-[#F5B301] bg-white p-6 shadow-sm">
            <SectionHead title="Upcoming" to="/live-scores" id="upcoming" />
            <div className="space-y-2">{data.upcoming.length ? data.upcoming.map((f) => <FixtureRow key={f.id} f={f} />) : <Empty text="No upcoming fixtures found in online sources yet." />}</div>
          </div>
        </div>
      </section>

      <section className="container-x py-14">
        <SectionHead title="Featured Athletes" to="/cameroonians-abroad" id="featured-athletes" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8">{data.featured_athletes.map((a) => <AthleteCard key={a.slug} a={a} showSport={false} />)}</div>
      </section>

      <section className="bg-[#0B1120] py-14 text-white">
        <div className="container-x">
          <div className="mb-8 flex items-end justify-between" data-testid="section-latest-videos">
            <h2 className="section-title section-title-light">{t("Latest Videos")}</h2>
            <Link to="/videos" className="flex items-center gap-1 text-sm font-semibold text-slate-300 hover:text-[#F5B301]" data-testid="section-latest-videos-link">{t("View All")} <ArrowRight size={16} /></Link>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{data.videos.length ? data.videos.map((v) => <VideoCard key={v.video_id} v={v} dark />) : <Empty />}</div>
        </div>
      </section>

      <AppPromo />
      <Newsletter />
    </div>
  );
}
